/**
 * Mapbox
 *
 * Build the Mapbox JS map with pins for locations
 */

/* global L */

import $ from 'jquery';
import 'mapbox.js';
import 'leaflet.markercluster';
import debounce from 'lodash/debounce';

var accessToken = window.provenance.mapboxAccessToken;
var username = window.provenance.mapboxUsername;
var linkIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
                  <g fill="currentColor" fillRule="evenodd">
                    <path
                      d="M11.257 0H7.132a.74.74 0 0 0 0 1.479h2.34L5.57 5.382a.734.734 0 0 0-.217.523c0 .197.077.383.217.523s.325.216.523.216a.735.735 0 0 0 .523-.216l3.903-3.903v2.34a.74.74 0 0 0 1.479 0V.738a.74.74 0 0 0-.74-.739z"
                    />
                    <path
                      d="M8.499 10.73H1.268V3.497h5.438L7.974 2.23H.634A.634.634 0 0 0 0 2.864v8.499c0 .35.284.634.634.634h8.499c.35 0 .633-.284.633-.634v-7.34L8.5 5.291v5.438z"
                    />
                  </g>
                </svg>`;

/**
 * Photo clusters map
 *
 * For displaying multiple pins in culsters according to the photo locations
 */

function initPhotoClustersMap(el) {
  var $map = $(el);
  var $mapOverlay = $map.next('.Mapbox-overlay');
  var geojsonUrl = $map.data('geojson');
  var styleID = $map.data('styleid');
  var map;
  var markers;

  L.mapbox.accessToken = accessToken;

  map = L.mapbox.map('Mapbox-map--photoClusters', null, {
    attributionControl: false,
    maxZoom: 16,
    scrollWheelZoom: false,
    // disable standard zoom control
    zoomControl: false
  });

  // style the underlying map
  L.mapbox.styleLayer(`mapbox://styles/${username}/${styleID}`).addTo(map);

  markers = L.mapbox.featureLayer().loadURL(geojsonUrl);

  markers.on('ready', e => {
    // initialise clusters
    var clusterGroup = new L.MarkerClusterGroup({
      iconCreateFunction(cluster) {
        var childCount = cluster.getChildCount();

        // enforce a limit of 99 because 3 digits don't fit in our pins
        if (childCount >= 99) {
          childCount = 99;
        }

        return L.mapbox.marker.icon({
          'marker-symbol': childCount,
          'marker-color': '#ee6265'
        });
      }
    });

    // customise the popups
    e.target.eachLayer(layer => {
      /* eslint-disable max-len */
      var data = layer.feature.properties;
      var flagIcon =
        '<svg class="Mapbox-flagIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M9.117 37.142V31.33c1.432.362 4.946.54 6.43.54 3.458 0 6.618-.96 9.675-1.892 3.134-.952 6.093-1.852 9.356-1.852 2.4 0 4.746.495 7.275 1.55l.13.045s.242.07.354.096c.173.04.373.062.58.062 1.033 0 1.74-.475 1.83-1.22l.012-.033-.002-24.687c-.023-1.164-.697-1.73-1.17-2.003C40.59.635 37.647 0 34.59 0c-3.918 0-7.42 1.066-10.512 2.007-2.903.88-5.645 1.716-8.524 1.716-1.502 0-5.015-.228-6.44-.694v-.96l-.533-.123c-.213-.05-.446-.075-.693-.075-.61 0-1.2.152-1.58.406-.426.282-.697.625-.883 1.118-.07.192-.12.388-.165.583l-.02.168v42.674c0 .185.035.51.07.697.074.403.19 1.015.59 1.552.307.414.762.732 1.217.852.193.05.384.076.568.076.485 0 .897-.178 1.19-.514.198-.23.232-.485.24-.622V37.142z"/></svg>';
      var content = '';
      if (data.stage_title) {
        content += `<p class="Mapbox-markerTitle Text-bodySansSmaller">${data.stage_title}</p>`;
      }
      content += `
        <div class="FlexEmbed FlexEmbed--3by2">
          <div class="FlexEmbed-item">
            <a class="Mapbox-markerInner" href="${data.url}">
              <img class="Mapbox-markerImage" src="${data.image}" alt="">
            </a>
            <p class="Mapbox-markerLocation Text-bodySansSmaller">${flagIcon} ${data.location}</p>
          </div>
        </div>
      `;
      layer.bindPopup(content, {
        closeButton: false,
        maxWidth: 195,
        minWidth: 195
      });
      clusterGroup.addLayer(layer);
    });

    // add clusters to map
    map.addLayer(clusterGroup);

    // make sure all pins are visible
    map.fitBounds(markers.getBounds().pad(0.1), {
      maxZoom: 12
    });

    $map.addClass('is-loaded');
  });

  // show popups on hover
  markers.on('mouseover', e => {
    e.layer.openPopup();
  });

  // event listener to hide overlay and enable zoom controls
  $mapOverlay.on('click', function(e) {
    var zoomControl;

    e.preventDefault();
    $(this).addClass('is-hidden');
    zoomControl = L.control.zoom({ position: 'bottomright' });
    zoomControl.addTo(map);
  });

  $(window).resize(
    debounce(() => {
      map.fitBounds(markers.getBounds().pad(0.25), {
        maxZoom: 12
      });
    }, 300)
  );
}

/**
 * Avatars map
 *
 * For displaying avatars of multiple users on a map
 */

function initAvatarsMap(el) {
  var $map = $(el);
  var $mapOverlay = $map.next('.Mapbox-overlay');
  var styleID = $map.data('styleid');
  var geojsonUrl = $map.data('geojson');
  var map;
  var markers;

  L.mapbox.accessToken = accessToken;

  map = L.mapbox.map('Mapbox-map--avatars', null, {
    attributionControl: false,
    legendControl: {
      position: 'topleft'
    },
    maxZoom: 16,
    scrollWheelZoom: false,
    // disable standard zoom control
    zoomControl: false
  });

  // style the underlying map
  L.mapbox.styleLayer(`mapbox://styles/${username}/${styleID}`).addTo(map);

  markers = L.mapbox
    .featureLayer()
    .loadURL(geojsonUrl)
    .addTo(map)
    // set a custom icon on each marker based on feature properties.
    .on('layeradd', e => {
      var marker = e.layer;

      if (marker.feature.properties.icon) {
        marker.setIcon(L.icon(marker.feature.properties.icon));
      }
      marker.options.riseOnHover = true;
    })
    // make sure clicked marker appears above others
    .on('popupopen', e => {
      var marker = e.layer;

      marker.setZIndexOffset(250);
    })
    // return clicked marker to previous position
    .on('popupclose', e => {
      var marker = e.layer;

      marker.setZIndexOffset(0);
    });

  markers.on('ready', e => {
    // make sure all pins are visible
    map.fitBounds(markers.getBounds().pad(0.25), {
      maxZoom: 12
    });
    $map.addClass('is-loaded');

    // customise the popups
    e.target.eachLayer(layer => {
      /* eslint-disable max-len */
      var data = layer.feature.properties;
      var flagIcon =
        '<svg class="Mapbox-flagIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50"><path d="M9.117 37.142V31.33c1.432.362 4.946.54 6.43.54 3.458 0 6.618-.96 9.675-1.892 3.134-.952 6.093-1.852 9.356-1.852 2.4 0 4.746.495 7.275 1.55l.13.045s.242.07.354.096c.173.04.373.062.58.062 1.033 0 1.74-.475 1.83-1.22l.012-.033-.002-24.687c-.023-1.164-.697-1.73-1.17-2.003C40.59.635 37.647 0 34.59 0c-3.918 0-7.42 1.066-10.512 2.007-2.903.88-5.645 1.716-8.524 1.716-1.502 0-5.015-.228-6.44-.694v-.96l-.533-.123c-.213-.05-.446-.075-.693-.075-.61 0-1.2.152-1.58.406-.426.282-.697.625-.883 1.118-.07.192-.12.388-.165.583l-.02.168v42.674c0 .185.035.51.07.697.074.403.19 1.015.59 1.552.307.414.762.732 1.217.852.193.05.384.076.568.076.485 0 .897-.178 1.19-.514.198-.23.232-.485.24-.622V37.142z"/></svg>';
      var content = `
        <div class="Mapbox-markerTitle Text-bodySansSmall">${data.title}</div>
        <div class="Mapbox-markerLocation Text-bodySansSmallest">${flagIcon} ${data.location}</div>
      `;
      if (data.url) {
        content += `<div class="Mapbox-markerLink Text-bodySansSmall"><a class="ExternalLink-inner" href="${data.url}" rel="nofollow noopener" target="_blank">View profile ${linkIcon}</a></div>`;
      }
      layer.bindPopup(content, {
        closeButton: false
      });
      markers.addLayer(layer);
    });
  });

  if ($('.Mapbox-legend').length) {
    map.legendControl.addLegend($('.Mapbox-legend').html());
  }

  // event listener to hide overlay and enable zoom controls
  $mapOverlay.on('click', function(e) {
    var zoomControl;

    e.preventDefault();
    $(this).addClass('is-hidden');
    zoomControl = L.control.zoom({ position: 'bottomright' });
    zoomControl.addTo(map);
  });

  $(window).resize(
    debounce(() => {
      map.fitBounds(markers.getBounds().pad(0.25), {
        maxZoom: 12
      });
    }, 300)
  );
}

/**
 * Valid regions maps
 *
 * A map of the world showing where a scheme is valid
 */

function initValidRegionsMap(el) {
  var $map = $(el);
  var $mapOverlay = $map.next('.Mapbox-overlay');
  var geojson = $map.data('geojson');
  var styleID = $map.data('styleid');
  var map;

  L.mapbox.accessToken = accessToken;

  map = L.mapbox
    .map('Mapbox-map--validRegions', null, {
      attributionControl: false,
      legendControl: {
        position: 'topleft'
      },
      scrollWheelZoom: false,
      // disable standard zoom control
      zoomControl: false
    })
    .setView([40, 0], 2);

  // style the underlying map
  L.mapbox.styleLayer(`mapbox://styles/${username}/${styleID}`).addTo(map);

  L.geoJson(geojson, {
    style() {
      return {
        color: '#2a2a2a',
        fillOpacity: 0.7,
        fillColor: '#2f3a4a',
        opacity: 0.1,
        weight: 2
      };
    }
  }).addTo(map);

  $map.addClass('is-loaded');

  if ($('.Mapbox-legend').length) {
    map.legendControl.addLegend($('.Mapbox-legend').html());
  }

  // event listener to hide overlay and enable zoom controls
  $mapOverlay.on('click', function(e) {
    var zoomControl;

    e.preventDefault();
    $(this).addClass('is-hidden');
    zoomControl = L.control.zoom({ position: 'bottomright' });
    zoomControl.addTo(map);
  });
}

/**
 * Single location map
 */

function initSingleLocationMap(el, id, isInteractive) {
  const $map = $(`#${id}`);
  const $mapOverlay = $map.next('.Mapbox-overlay');
  const styleID = $map.data('styleid');
  const coordinatesData = $map.data('coordinates');
  let coordinates = coordinatesData;
  let zoom = 12;
  if (coordinatesData === 'unknown') {
    coordinates = [0, 0];
    zoom = 1;
  }
  const longitude = coordinates[0];
  const latitude = coordinates[1];
  const latSymbol = latitude >= 0 ? 'N' : 'S';
  const lngSymbol = longitude >= 0 ? 'E' : 'W';
  const formattedLat = Math.abs(latitude.toFixed(6));
  const formattedLng = Math.abs(longitude.toFixed(6));
  const markerTitle = `${latSymbol}: ${formattedLat}, ${lngSymbol}: ${formattedLng}`;

  L.mapbox.accessToken = accessToken;

  const map = L.mapbox.map(id, null, {
    attributionControl: false,
    maxZoom: 16,
    scrollWheelZoom: false,
    // disable standard zoom control
    zoomControl: false
  });

  // style the underlying map
  L.mapbox.styleLayer(`mapbox://styles/${username}/${styleID}`).addTo(map);

  if (coordinatesData !== 'unknown' && isInteractive) {
    // add a single marker if coordinates are given
    L.mapbox
      .featureLayer({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude]
        },
        properties: {
          title: markerTitle,
          'marker-color': '#ee6265'
        }
      })
      .addTo(map);
  }

  map.setView([latitude, longitude], zoom);
  $map.addClass('is-loaded');

  // event listener to hide overlay and enable zoom controls
  if (isInteractive) {
    $mapOverlay.on('click', function(e) {
      var zoomControl;

      e.preventDefault();
      $(this).addClass('is-hidden');
      zoomControl = L.control.zoom({ position: 'bottomright' });
      zoomControl.addTo(map);
    });
  }

  // custom event listener to invalidate the map
  $map.on('mapbox.invalidateSize', () => {
    map.invalidateSize();
  });
}

/**
 * Initialise module
 */

function init() {
  // return early if no Mapbox module present
  if (!$('.Mapbox').length) {
    return;
  }

  $('.Mapbox-map').each((i, el) => {
    // render the different types of map available
    if ($(el).hasClass('Mapbox-map--photoClusters')) {
      initPhotoClustersMap(el);
    } else if ($(el).hasClass('Mapbox-map--avatars')) {
      initAvatarsMap(el);
    } else if ($(el).hasClass('Mapbox-map--validRegions')) {
      initValidRegionsMap(el);
    } else if ($(el).hasClass('Mapbox-map--singleLocation')) {
      initSingleLocationMap(el, el.id, true);
    } else if ($(el).hasClass('Mapbox-map--nonInteractive')) {
      initSingleLocationMap(el, el.id, false);
    }
  });
}

$(document).ready(init);

export { init, initSingleLocationMap };
